import PropTypes from 'prop-types';
import jwtDecode from 'jwt-decode';
import { get, intersection, isEmpty, remove, set } from 'lodash';
import { createContext, useState, useEffect } from 'react';
import { getRequest, postRequest } from '../utils/request';
import { forgotPasswordInit, forgotPasswordChallenge, verifyEmailInit, verifyEmailChallenge } from '../api/users';

export const AuthContext = createContext({
    isAuthenticated: false,
    setIsAuthenticated: () => { },
    isInitialized: true,
    setIsInitialized: () => { },
    isNewPasswordRequired: false,
    setIsNewPasswordRequired: () => { },
    user: null,
    setUser: () => { },
    config: {},
    setConfig: () => { },
    prevOthersConfig: {},
    setPrevOthersConfig: () => { },
    section: null,
    setSection: () => { },
    login: () => Promise.resolve(),
    logout: () => Promise.resolve(),
    signUp: () => Promise.resolve(),
    resendSignUp: () => Promise.resolve(),
    confirmSignUp: () => Promise.resolve(),
    validate: () => Promise.resolve(),
    forgotPassword: () => Promise.resolve(),
    forgotPasswordSubmit: () => Promise.resolve(),
    verifyEmail: () => Promise.resolve(),
    verifyEmailSubmit: () => Promise.resolve(),
    completeNewPassword: () => Promise.resolve(),
    whoAmI: null,
    isAcceptedTerms: null,
    isEmailVerified: null,
})


export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [config, setConfig] = useState({});
    const [prevOthersConfig, setPrevOthersConfig] = useState(null)
    const [isInitialized, setIsInitialized] = useState(false)
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isAcceptedTerms, setIsAcceptedTerms] = useState(null)
    const [isEmailVerified, setIsEmailVerified] = useState(null)
    const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false)
    const [section, setSection] = useState()
    const [token, setToken] = useState(null)
    const [whoAmI, setWhoAmI] = useState(null)
    const [isAllowed, setIsAllowed] = useState(false)

    // check if jwt token exists
    useEffect(() => {
        if (isEmpty(section)) return
        const jwt = localStorage.getItem('jwt');
        if (jwt) {
            const user = jwtDecode(jwt);
            setUser(user);
        } else console.log('no jwt token')
    }, [section])

    // check if user is allowed in the section
    useEffect(() => {
        if (isEmpty(user)) return
        if (isEmpty(section)) return
        const isAllowed = checkUserAllowedInSection(user)
        setIsAllowed(isAllowed)
    }, [user])

    useEffect(() => {
        if (!isAllowed) {
            console.log('user not allowed in section')
            setIsAuthenticated(false)
        } else {
            console.log('user allowed in section')
            setIsAuthenticated(true);
            getAuthMe()
        }
        setIsInitialized(true)
    }, [isAllowed])

    const checkUserAllowedInSection = (user) => {
        // define the roles vs section dictionary
        const allowedRolesDict = {
            // manager: ['admin', 'clinicManager'],
            // inventory: ['admin', 'inventoryManager'],
            // therapist: ['therapist'],
            // doctor: ['doctor'],
            // employee: ['employee', 'receptionist'],
            employee: ['admin', 'clinicManager', 'inventoryManager', 'doctor', 'therapist', 'employee', 'receptionist', 'hr'],
            patient: ['patient'],
        };

        // check user role vs section
        const roles = user.roles.map(role => role.role)
        return intersection(roles, allowedRolesDict[section]).length > 0
    }

    const getAuthMe = async () => {
        let isAcceptedTerms = false
        let isEmailVerified = false
        try {
            const response = await getRequest(`/user/me`)
            const userRoleArray = response.roles.map(role => role.role)
            const whoAmIData = {
                ...response,
                userRoleArray,
                isAdmin: userRoleArray.includes('admin'),
                isEmployee: userRoleArray.includes('employee'),
                isTherapist: userRoleArray.includes('therapist'),
                isDoctor: userRoleArray.includes('doctor'),
                isInventoryManager: userRoleArray.includes('inventoryManager') || userRoleArray.includes('admin'),
            }
            // console.log('whoAmIData', whoAmIData)
            setWhoAmI(whoAmIData)
            isAcceptedTerms = response ? response?.acceptedTerms : false
            isEmailVerified = response ? response?.emailVerified : false
        } catch (error) {
            // handle if jwt token is invalid
            console.log('getAuthMe', error)
            // remove jwt token
            localStorage.removeItem('jwt');
            setIsAuthenticated(false)
        }
        setIsAcceptedTerms(isAcceptedTerms)
        setIsEmailVerified(isEmailVerified)

    }

    const login = async (email, password, section) => {
        const response = await postRequest('/auth/login', { email, password, section });
        const { token, user } = response

        // handle jwt token
        localStorage.setItem('jwt', token);
        const decoded = jwtDecode(token);
        await getAuthMe()
        setUser(decoded);
        setIsAuthenticated(true);
    }

    const logout = () => {
        localStorage.removeItem('jwt');
        setUser(null);
        setIsAuthenticated(false);
    }

    const forgotPassword = async (email) => {
        await forgotPasswordInit(email);
    }
    const forgotPasswordSubmit = async (email, code, newPassword, newPasswordConfirm) => {
        await forgotPasswordChallenge(email, code, newPassword, newPasswordConfirm);
    }

    const verifyEmail = async (email) => {
        await verifyEmailInit(email);
    }

    const verifyEmailSubmit = async (email, code) => {
        await verifyEmailChallenge(email, code);
    }

    // const completeNewPassword = async (username, password) => {
    //     // logic here
    //     setIsNewPasswordRequired(false)
    // }

    // sign up using jwt
    const signUp = async (data) => {
        const response = await postRequest('/auth/register', data);
        console.log('response', response)
        // handle jwt token
        const { token, user } = response
        localStorage.setItem('jwt', token);
        const decoded = jwtDecode(token);
        setUser(decoded);
        setIsAuthenticated(true);
    }

    // const resendSignUp = async (username) => {
    //     await Auth.resendSignUp(username);
    // };

    // const confirmSignUp = async (username, code) => {
    //     await Auth.confirmSignUp(username, code);
    // };

    const value = {
        user,
        setUser,
        config,
        setConfig,
        prevOthersConfig,
        setPrevOthersConfig,
        isAuthenticated,
        setIsAuthenticated,
        isInitialized,
        setIsInitialized,
        isNewPasswordRequired,
        setIsNewPasswordRequired,
        section,
        setSection,
        signUp,
        // resendSignUp,
        // confirmSignUp,
        login,
        logout,
        forgotPassword,
        forgotPasswordSubmit,
        verifyEmail,
        verifyEmailSubmit,
        // completeNewPassword,
        whoAmI,
        getAuthMe,
        isAcceptedTerms,
        isEmailVerified,
    }
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

AuthProvider.propTypes = {
    children: PropTypes.node,
};